import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaEllipsisV } from 'react-icons/fa';

const BLOCK = 'LStudio__app_table';

/*
    🔥 MENÚ DE ACCIONES DE FILA

    ⚠️ CUATRO PROBLEMAS QUE ESTE COMPONENTE RESUELVE

    1. EL RECORTE. Un menú absoluto dentro de un <td> queda cortado por
       el overflow del contenedor. Por eso va a un portal.

    2. EL ALTO. Con once acciones mide más de 450px. Si no entra, se
       le pone un máximo según el espacio real y scrollea adentro.

    3. LA PROFUNDIDAD. El proyecto ya tenía z-index de hasta
       2.147.483.647 (AppToaster) y 101.000 (la tabla vieja). Un menú
       en 1.800 pierde contra todo eso: por eso parecía que no abría.
       La capa se resuelve por variable CSS por instancia, incluso frente a reglas globales !important.

    4. EL SCROLL — y acá estaba el bug nuevo.

       ⚠️ SCROLLEAR EL MENÚ LO CERRABA A SÍ MISMO

       El listener de scroll está en `window` con capture en true. Ese
       true es necesario para enterarse del scroll de CUALQUIER
       contenedor, no solo del window. Pero también captura el scroll
       DEL PROPIO MENÚ: mover la rueda adentro lo cerraba.

       Ahora se ignora todo scroll que nazca dentro del menú.

       ⚠️ Y AL SCROLLEAR LA PÁGINA, SE REPOSICIONA EN VEZ DE CERRARSE

       Cerrar de golpe es molesto: el usuario baja un poco para ver
       mejor y pierde el menú. Lo correcto es que lo siga. Solo se
       cierra cuando el botón que lo abrió sale de la pantalla, porque
       ahí el menú quedaría flotando huérfano.
*/

const ITEM_HEIGHT = 38;
const DIVIDER_HEIGHT = 11;
const MENU_PADDING = 14;
const SAFE_MARGIN = 12;

/*
    Por encima del panel de desarrollo (400.000) y de la tabla vieja
    (101.000). Por debajo de los modales (600.000) y del toaster.
*/
const Z_INDEX = 500000;
const MODAL_Z_INDEX = 650000;

export const TableActionMenu = ({
    items = [],
    width = 220,
    label = 'Acciones',
    isDisabled = false,
    triggerLabel = null,
    triggerIcon = null,
    buttonClassName = '',
    portalZIndex = null,
}) => {

    const REF__useRefButton = useRef(null);
    const REF__useRefMenu = useRef(null);
    const REF__useRefFrame = useRef(null);

    const [useIsOpen, setIsOpen] = useState(false);
    const [useBox, setBox] = useState({ top: 0, left: 0, maxHeight: 400, isUp: false });

    /*
        🎯 Si el trigger vive dentro de un AppModal, el menú portalizado debe
        quedar por encima del modal (600k) y por debajo de confirmaciones (700k).

        V14: la capa se publica también en --LS__table_action_portal_z. El CSS
        global usa esa variable dentro de su regla !important; así el z-index
        particular del modal ya no queda pisado por --LS__z_float (500k).
    */
    const RESOLVED_PORTAL_Z_INDEX = portalZIndex ?? (
        REF__useRefButton.current?.closest?.('.LStudio__app_modal') ? MODAL_Z_INDEX : Z_INDEX
    );

    const CB__onCalcPosition = useCallback(() => {

        const BUTTON = REF__useRefButton.current;

        if (!BUTTON) return true;

        const RECT = BUTTON.getBoundingClientRect();

        /*
            🔥 Si el botón se fue de la pantalla, el menú no tiene a
               qué anclarse: hay que cerrarlo.
        */
        const IS_VISIBLE = RECT.bottom > 0 && RECT.top < window.innerHeight;

        if (!IS_VISIBLE) return false;

        const REAL_ITEMS = items.filter((i) => !i.isDivider).length;
        const DIVIDERS = items.filter((i) => i.isDivider).length;

        const WANTED = REAL_ITEMS * ITEM_HEIGHT + DIVIDERS * DIVIDER_HEIGHT + MENU_PADDING;

        const SPACE_BELOW = window.innerHeight - RECT.bottom - SAFE_MARGIN;
        const SPACE_ABOVE = RECT.top - SAFE_MARGIN;

        /*
            Se abre hacia abajo si ahí entra completo. Si no entra en
            ninguno de los dos lados, se elige el que tenga MÁS espacio
            y el menú scrollea adentro.
        */
        const FITS_BELOW = WANTED <= SPACE_BELOW;

        const IS_UP = !FITS_BELOW && SPACE_ABOVE > SPACE_BELOW;

        const AVAILABLE = IS_UP ? SPACE_ABOVE : SPACE_BELOW;

        const HEIGHT = Math.min(WANTED, AVAILABLE);

        setBox({
            isUp: IS_UP,
            maxHeight: Math.max(160, HEIGHT),
            top: IS_UP ? Math.max(SAFE_MARGIN, RECT.top - HEIGHT - 6) : RECT.bottom + 6,
            // Nunca se sale por el costado derecho
            left: Math.min(window.innerWidth - width - 8, Math.max(8, RECT.right - width)),
        });

        return true;

    }, [items, width]);

    const onToggle = () => {
        if (!useIsOpen) CB__onCalcPosition();
        setIsOpen((prev) => !prev);
    };

    useEffect(() => {

        if (!useIsOpen) return;

        const onClickOutside = (event) => {
            if (REF__useRefMenu.current?.contains(event.target)) return;
            if (REF__useRefButton.current?.contains(event.target)) return;
            setIsOpen(false);
        };

        const onEscape = (event) => { if (event.key === 'Escape') setIsOpen(false); };

        /*
            🔥 EL SCROLL

            ⚠️ Primero: si el scroll nace DENTRO del menú, no se hace
               nada. Es el usuario recorriendo las acciones.

            ⚠️ Segundo: si nace afuera, se reposiciona en el siguiente
               cuadro. requestAnimationFrame evita recalcular decenas
               de veces por segundo y que el menú tiemble.
        */
        const onScroll = (event) => {

            if (REF__useRefMenu.current?.contains(event.target)) return;

            if (REF__useRefFrame.current) cancelAnimationFrame(REF__useRefFrame.current);

            REF__useRefFrame.current = requestAnimationFrame(() => {
                const STILL_VISIBLE = CB__onCalcPosition();
                if (!STILL_VISIBLE) setIsOpen(false);
            });
        };

        const onResize = () => {
            const STILL_VISIBLE = CB__onCalcPosition();
            if (!STILL_VISIBLE) setIsOpen(false);
        };

        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onEscape);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);

        return () => {
            if (REF__useRefFrame.current) cancelAnimationFrame(REF__useRefFrame.current);
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onEscape);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };

    }, [useIsOpen, CB__onCalcPosition]);

    const onPick = (item) => {
        setIsOpen(false);
        item.onClick?.();
    };

    /*
        🔥 Cada item se dibuja como enlace o como botón según tenga
           `to`. Mismo aspecto, semántica distinta.
    */
    const onRenderItem = (item, index) => {

        if (item.isDivider) return <span key={`div_${index}`} className={`${BLOCK}__float_divider`} />;

        const CONTENT = (
            <>
                {item.icon && <span className={`${BLOCK}__float_icon`}>{item.icon}</span>}
                <span className={`${BLOCK}__float_text`}>{item.label}</span>
                {item.hint && <small>{item.hint}</small>}
            </>
        );

        if (item.to && !item.isDisabled) {
            return (
                <Link key={item.id ?? item.label} to={item.to} role="menuitem"
                    className={`${BLOCK}__float_item`} data-tone={item.tone}
                    onClick={(event) => { setIsOpen(false); item.onClick?.(event); }} >
                    {CONTENT}
                </Link>
            );
        }

        return (
            <button key={item.id ?? item.label} type="button" role="menuitem"
                className={`${BLOCK}__float_item`} data-tone={item.tone}
                disabled={item.isDisabled}
                onClick={() => onPick(item)} >
                {CONTENT}
            </button>
        );
    };

    return (
        <>
            <button ref={REF__useRefButton} type="button"
                className={`${BLOCK}__menu_button ${triggerLabel ? `${BLOCK}__menu_button--labeled` : ''} ${buttonClassName}`.trim()}
                data-open={useIsOpen}
                aria-label={label} aria-haspopup="menu" aria-expanded={useIsOpen}
                disabled={isDisabled} onClick={onToggle} >
                {triggerLabel ? (
                    <>
                        {triggerIcon && <span className={`${BLOCK}__menu_button_icon`}>{triggerIcon}</span>}
                        <span>{triggerLabel}</span>
                        <FaChevronDown className={`${BLOCK}__menu_button_chevron`} />
                    </>
                ) : <FaEllipsisV />}
            </button>

            {
                useIsOpen && createPortal(
                    <div ref={REF__useRefMenu} className={`${BLOCK}__float_menu`} role="menu"
                        data-up={useBox.isUp}
                        /*
                            ⚠️ Sin esto, la rueda del mouse sobre el menú
                               sigue moviendo la página de atrás cuando el
                               menú ya llegó al final de su scroll.
                        */
                        onWheel={(event) => event.stopPropagation()}
                        style={{
                            top: useBox.top,
                            left: useBox.left,
                            width,
                            maxHeight: useBox.maxHeight,
                            zIndex: RESOLVED_PORTAL_Z_INDEX,
                            '--LS__table_action_portal_z': String(RESOLVED_PORTAL_Z_INDEX),
                        }} >
                        {items.map(onRenderItem)}
                    </div>,
                    document.body,
                )
            }
        </>
    );
};
